// ----------------------------------------------------------------------
// <copyright file="Izenda.FilterValuesColumn.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * Filter Value Column render function
 */
import React from 'react';
import { FILTER_TYPE, QUERY_SOURCE_TYPE } from 'logic/data/Izenda.QuerySourceTypeConfig';
import QuerySourceFieldDetailsStore from 'logic/Izenda.QuerySourceFieldDetailsStore';
import { FILTER_LOOKUP_STATUSES } from 'logic/data/Izenda.FilterLookupConstants';
import { flow, map, join, noop, toString } from 'lodash/fp';
import localizer from 'share/localization/Izenda.Localizer';

/**
 * Render filter value column
 * @param {QuerySourceFieldDetailsStore} querySourceFieldStore
 */
function renderFilterValues(querySourceFieldStore: QuerySourceFieldDetailsStore) {
  const filterValue = querySourceFieldStore.getFilterValues();
  const { type } = filterValue;
  const { isDisableFilterLookup } = querySourceFieldStore;
  const { store, querySourceType } = this.props;
  const isInvalidFilterLookup = querySourceFieldStore.isInvalidFilterLookup;
  // Only support filter lookup for Table/Views/Param on Stored
  const showEditIcon = querySourceType !== QUERY_SOURCE_TYPE.STORED_PROC || querySourceFieldStore.isParameter;

  function showFilterValueDialog() {
    store.editingQuerySourceField = querySourceFieldStore;
    store.isEditingFilter = true;
  }

  /**
   * Render popup content when using user defined filter
   */
  function renderUserDefined() {
    const displayValue = filterValue.userDefinedValues.join();
    return (
      <div className='filterValueText' title={displayValue}>
        <span>{displayValue}</span>
      </div>
    );
  }

  /**
   * Render popup content when using lookup key
   */
  function renderDatabaseFilter() {
    const { querySourceName, lookupKeyQuerySourceFieldName, displayQuerySourceFieldName } = filterValue;
    const fieldName = `${lookupKeyQuerySourceFieldName}-${displayQuerySourceFieldName}`;
    const tooltip = `${querySourceName}\n${fieldName}`;

    return (
      <div className='filterValueText' title={tooltip}>
        {querySourceName}
        <br />
        {fieldName}
      </div>
    );
  }

  /**
   * Render popup content base on user type
   */
  function renderDisplayValue() {
    switch (toString(type)) {
      case FILTER_TYPE.DB_STRUCTURE:
        return renderDatabaseFilter();
      case FILTER_TYPE.USER_DEFINED:
        return renderUserDefined();
      default:
        return <div className='filterValueText' />;
    }
  }

  /**
   * Render invalid filter lookup icon
   */
  function renderInvalidFilterLookup() {
    const filterLookupStatus = querySourceFieldStore.getFilterLookupStatus();
    const {
      databaseName,
      querySourceName: querySourceCategoryAndName,
      lookupKeyQuerySourceFieldName,
      displayQuerySourceFieldName,
      querySourceCategoryName
    } = filterValue;

    // Build invalid field(s)
    const buildField = fieldType => {
      if (!querySourceCategoryName) {
        return [];
      }
      const querySourceName = querySourceCategoryAndName.substring(querySourceCategoryName.length + 1);
      const fullQuerySourceName = [databaseName, querySourceCategoryName, querySourceName];
      switch (fieldType) {
        case 'Lookup':
          return [...fullQuerySourceName, lookupKeyQuerySourceFieldName];
        case 'Display':
          return [...fullQuerySourceName, displayQuerySourceFieldName];
        case 'Both':
        default:
          return [...fullQuerySourceName, `${lookupKeyQuerySourceFieldName}]-[${displayQuerySourceFieldName}`];
      }
    };

    // Add bracket
    const buildFullPath = fieldType =>
      flow(
        map(part => `[${part}]`),
        join('.')
      )(buildField(fieldType));

    // Get tooltip text
    const getTooltip = () => {
      switch (filterLookupStatus) {
        case FILTER_LOOKUP_STATUSES.INVALID_CONNECTION:
          return String.format(
            localizer.getText('DATA_MODEL_TOOLTIP_INVALID_FILTER_LOOKUP_CONNECTION'),
            buildFullPath('Both')
          );
        case FILTER_LOOKUP_STATUSES.INVALID_QUERY_SOURCE:
          return String.format(
            localizer.getText('DATA_MODEL_TOOLTIP_INVALID_FILTER_LOOKUP_QUERY_SOURCE'),
            buildFullPath('Both')
          );
        case FILTER_LOOKUP_STATUSES.LOOKUP_IS_TENANT_FIELD:
          return String.format(
            localizer.getText('DATA_MODEL_TOOLTIP_INVALID_FILTER_TENANT_FIELD'),
            buildFullPath('Lookup')
          );
        case FILTER_LOOKUP_STATUSES.LOOKUP_INVISIBLE:
          return String.format(
            localizer.getText('DATA_MODEL_TOOLTIP_INVALID_FILTER_INVISIBLE_FIELD'),
            buildFullPath('Lookup')
          );
        case FILTER_LOOKUP_STATUSES.DISPLAY_IS_TENANT_FIELD:
          return String.format(
            localizer.getText('DATA_MODEL_TOOLTIP_INVALID_FILTER_TENANT_FIELD'),
            buildFullPath('Display')
          );
        case FILTER_LOOKUP_STATUSES.DISPLAY_INVISIBLE:
          return String.format(
            localizer.getText('DATA_MODEL_TOOLTIP_INVALID_FILTER_INVISIBLE_FIELD'),
            buildFullPath('Display')
          );
        case FILTER_LOOKUP_STATUSES.LOOKUP_IS_TENANT_FIELD | FILTER_LOOKUP_STATUSES.DISPLAY_IS_TENANT_FIELD:
          return String.format(
            localizer.getText('DATA_MODEL_TOOLTIP_INVALID_FILTER_TENANT_BOTH_FIELD'),
            buildFullPath('Both')
          );
        case FILTER_LOOKUP_STATUSES.LOOKUP_INVISIBLE | FILTER_LOOKUP_STATUSES.DISPLAY_INVISIBLE:
          return String.format(
            localizer.getText('DATA_MODEL_TOOLTIP_INVALID_FILTER_INVISIBLE_BOTH_FIELD'),
            buildFullPath('Both')
          );
        case FILTER_LOOKUP_STATUSES.LOOKUP_IS_TENANT_FIELD | FILTER_LOOKUP_STATUSES.DISPLAY_INVISIBLE:
          return `${String.format(
            localizer.getText('DATA_MODEL_TOOLTIP_INVALID_FILTER_TENANT_FIELD'),
            buildFullPath('Lookup')
          )}\n${String.format(
            localizer.getText('DATA_MODEL_TOOLTIP_INVALID_FILTER_INVISIBLE_FIELD'),
            buildFullPath('Display')
          )}`;
        case FILTER_LOOKUP_STATUSES.LOOKUP_INVISIBLE | FILTER_LOOKUP_STATUSES.DISPLAY_IS_TENANT_FIELD:
          return `${String.format(
            localizer.getText('DATA_MODEL_TOOLTIP_INVALID_FILTER_INVISIBLE_FIELD'),
            buildFullPath('Lookup')
          )}\n${String.format(
            localizer.getText('DATA_MODEL_TOOLTIP_INVALID_FILTER_TENANT_FIELD'),
            buildFullPath('Display')
          )}`;
        default:
          return '';
      }
    };

    return <span className='icon-info-circled filterLookupErrorIndicator' title={getTooltip()} />;
  }

  const onClick = isDisableFilterLookup ? noop : showFilterValueDialog;

  return (
    showEditIcon && (
      <div className='u-posRelative'>
        {renderDisplayValue()}
        {isInvalidFilterLookup && toString(type) === FILTER_TYPE.DB_STRUCTURE && renderInvalidFilterLookup()}
        <icon
          data-toggle='modal'
          className='btn icon-pencil filterValueIcon center'
          onClick={onClick}
          disabled={isDisableFilterLookup}
        />
      </div>
    )
  );
}

export default {
  render: renderFilterValues
};



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/DataModel/QuerySourceType/Izenda.FilterValuesColumn.js